import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Magnet, MousePointerClick, FileText, Gift, ArrowRightCircle, PlayCircle, X, Maximize2, Minimize2 } from 'lucide-react';
interface SlideSnakAnatomyProps {
  onContinue: () => void;
}
const STEPS = [{
  step: '1',
  title: 'Hook',
  desc: 'Earn attention immediately with a relevant question, claim or tension.',
  icon: Magnet
}, {
  step: '2',
  title: 'Choice',
  desc: 'Let the HCP select what matters to them through a quiz, poll, menu or pathway.',
  icon: MousePointerClick
}, {
  step: '3',
  title: 'Short content',
  desc: 'Deliver one focused scientific message at a time.',
  icon: FileText
}, {
  step: '4',
  title: 'Reward',
  desc: 'Provide useful feedback, proof, comparison or practical insight.',
  icon: Gift
}, {
  step: '5',
  title: 'Action',
  desc: 'Move the HCP toward the next relevant step.',
  icon: ArrowRightCircle
}];
export function SlideSnakAnatomy({
  onContinue
}: SlideSnakAnatomyProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [250, 600, 950, 1300, 1650, 2200].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-16 md:py-12 max-w-[1400px] mx-auto" data-id="element-1229" data-mp="2my6vcf0u9ztr">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-1230" data-mp="3jm3e9qjr1785">
        <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-1231" data-mp="3asqeq5r6j7d3">
          The Anatomy of a Snak
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-white" data-id="element-1232" data-mp="75hnqg8q1mtr">
          A Snak is designed around the effort–reward equation
        </h2>
        <p className="text-lg text-white/60" data-id="element-1233" data-mp="2dzl7nr1ve6o">
          Every interaction must make the next step feel worth the HCP's time.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center w-full mt-6 space-y-5" data-id="element-1234" data-mp="qa36p7kd3g5x">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(5,1fr)] gap-3 lg:gap-0 items-stretch" data-id="element-1235" data-mp="ciody90nk0lo">
          {STEPS.map((item, idx) => {
          const Icon = item.icon;
          const visible = stage >= idx + 1;
          return <motion.div key={item.step} initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: visible ? 1 : 0,
            y: visible ? 0 : 16
          }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-5 flex flex-col lg:mx-1.5" data-id="element-1236" data-mp="2ixio549phkj" data-mpi={idx}>
                <div className="flex items-center justify-between gap-2 mb-4" data-id="element-1237" data-mp="1nymroq6rzat8">
                  <div className="w-10 h-10 rounded-xl bg-teal-400/10 text-teal-400 flex items-center justify-center" data-id="element-1238" data-mp="1ry5exsm2jxz8">
                    <Icon className="w-5 h-5" data-id="element-1239" data-mp="3chqxz9xw2bmr" />
                  </div>
                  <span className="text-teal-400 font-bold text-xs" data-id="element-1240" data-mp="38gxauis9t0ux">
                    {item.step}
                  </span>
                </div>
                <h3 className="text-white font-semibold text-base mb-2" data-id="element-1241" data-mp="3m95789fn6pt3">
                  {item.title}
                </h3>
                <p className="text-white/60 text-[13px] leading-snug" data-id="element-1242" data-mp="3w4yqebnv9hju">
                  {item.desc}
                </p>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: stage >= 6 ? 1 : 0
      }} className="hidden lg:flex items-center justify-center gap-2 text-white/30 text-xs font-semibold uppercase tracking-widest" data-id="element-1243" data-mp="90s8gkbohv0w">
          <div className="h-px flex-1 bg-navy-600" data-id="element-1244" data-mp="25t29vtdbe4r3" />
          Low effort in · high relevance out
          <div className="h-px flex-1 bg-navy-600" data-id="element-1245" data-mp="287pi9as93z94" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 6 ? 1 : 0,
        y: stage >= 6 ? 0 : 12
      }} className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-4" data-id="element-1246" data-mp="287ford7zz9yo">
          <div className="bg-navy-900/50 border border-navy-700 rounded-2xl px-6 py-5" data-id="element-1247" data-mp="2m6yq9n6azn20">
            <p className="text-white/85 text-base leading-relaxed" data-id="element-1248" data-mp="1ol1e07iiodyi">
              A Snak turns passive content into a{' '}
              <span className="text-white font-semibold" data-id="element-1249" data-mp="1tgqfwhi0e6xz">
                short, personalized and measurable experience
              </span>
              .
            </p>
          </div>
          <div className="bg-teal-400/[0.07] border border-teal-400/40 rounded-2xl px-6 py-5 flex items-center" data-id="element-1250" data-mp="239xftmtzcuqo">
            <p className="text-white text-base md:text-lg font-serif leading-snug" data-id="element-1251" data-mp="15r4fxrttbnox">
              Low effort for the HCP.{' '}
              <span className="text-teal-400" data-id="element-1252" data-mp="138iuuci5hrqe">High relevance in return.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}